import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { adminHasPermission, cleanUpGuarantees } from '../../../../utils/helpers';
import useCustomQuery from '../../../../network/useCustomQuery';
import useGetQueryParams from '../../../../customHooks/useGetQueryParams';
import { GET_ALL_ADMIN_GUARANTEES } from '../../../../graphql/admin_queries';
import useToast from '../../../../customHooks/useToast';
import useTableColumn from '../../../../customHooks/useTableColumn';
import generatePDF from '../../../../utils/generatePdf';
import generateCSV from '../../../../utils/generateCSV';
import { useGlobalState } from '../../../../contexts';

import AdminTable from '../../../../components/AdminTable';
import DashboardHeader from '../../../../components/DashboardHeader';
import AdminLinkTabs from '../../../../components/AdminLinkTabs';
import useFilterMethods from '../../../../customHooks/useFilterMethods';
import EmptyRecord from '../../../../components/EmptyRecord';
import RolesAndPermissionGuard from '../../../../components/RolesAndPermissionGuard';
import PermissionDenied from '../../../PermissionDenied';
import CustomerTabs from '../../../AdminCustomers/CustomerTabs';

import EmptyMerchantSettlement from '../../../../svgs/EmptyMerchantSettlement';
import HorizontalListIcon from '../../../../svgs/HorizontalListIcon';
import ActiveMerchantIcon from '../../../../svgs/ActiveMerchantIcon';
import DeactivateMerchantIcon from '../../../../svgs/DeactivateMerchantIcon';

const limit = 14;
interface IMerchantTableRecord{
defaultUrl?:string;
toggleStorageId?:string;
tabStatus?:string;
hasNameAndImage?:boolean;
permissionName:string;
payload?:any;
userId?:string;
subTab?:any;
TabsRaw?: any;
type?:string;
optionalColumn?: any
optionalColumnLabelFormat?: any
}
const GuaranteesTableRecords = ({
  defaultUrl,
  toggleStorageId,
  tabStatus,
  hasNameAndImage,
  permissionName,
  payload,
  userId,
  subTab,
  TabsRaw,
  type,
  optionalColumn,
  optionalColumnLabelFormat,
}:IMerchantTableRecord) => {
  const Toast = useToast();
  const { profile } = useGlobalState();
  const adminPermissions = profile?.role?.permissions || [];
  const [getGuarantees, getGuaranteesLoading, guarantees] = useCustomQuery({
    query: GET_ALL_ADMIN_GUARANTEES,
    onError: () => {},
    onData: () => {},
  });

  const Tabs = TabsRaw?.map((tab) => ({
    ...tab,
    disabled: !adminHasPermission(adminPermissions, tab.permission),
  }));

  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'property',
    'landlord',
    'amount',
    'annualIncome',
    'dateApplied',
    ...(optionalColumn || []),
    'status',
  ];
  const columnLabelFormat = {
    amount: 'Guarantee Amount',
    annualIncome: 'Annual Income',
    name: 'Customer',
    status: 'Status',
    dateApplied: 'Date Applied',
    ...(optionalColumnLabelFormat || {}),
    property: 'Property',
  };
  const {
    getColumns,
    applyChanges,
    selectedColumns,
  } = useTableColumn(allColumns, toggleStorageId);

  const [guaranteesRec, setguaranteesRec] = useState(null);
  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`${defaultUrl}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    push(`${defaultUrl}?page=1&search=${encodeURIComponent(e.target.value)}`);
  };

  const handlegetAllCaGuarantees = useCallback(() => {
    if (page) {
      const result = getFilterQueries();
      getGuarantees({
        variables: tabStatus ? {
          offset: (page - 1) * limit,
          limit,
          statusFilter: tabStatus,
          guaranteeType: type || 'default',
          search,
          userId,
          filters: {
            from: result.from,
            to: result.to,
            tenor: result?.tenor,
            channel: result?.partner,
            status: result?.status,
            minAmount: result?.min ? Number(result?.min) : 0,
            maxAmount: result?.max ? Number(result?.max) : 0,
          },
        } : {
          offset: (page - 1) * limit,
          limit,
          guaranteeType: type || 'default',
          search,
          userId,
          filters: {
            from: result.from,
            to: result.to,
            // tenor: result?.tenor,
            status: result?.status,
            minAmount: result?.min ? Number(result?.min) : 0,
            maxAmount: result?.max ? Number(result?.max) : 0,
          },
        },
      });
    }
  }, [page, search]);

  useEffect(() => {
    handlegetAllCaGuarantees();
  }, [handlegetAllCaGuarantees]);

  const onFilterSubmit = (result) => {
    getGuarantees({
      variables: {
        offset: 0,
        limit,
        ...(tabStatus ? { statusFilter: tabStatus } : {}),
        search,
        guaranteeType: type || 'default',
        filters: {
          from: result.from,
          to: result.to,
          // tenor: result?.tenor,
          status: result.status,
          minAmount: result.min ? Number(result.min) : 0,
          maxAmount: result.max ? Number(result.max) : 0,
        },
      },
    });
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: ['dateRange', ...((!tabStatus) ? ['status'] : []), 'amountRange'],
    defaultUrl,
    cb: onFilterSubmit,
    Toast,
    customFilterInformation: {
      dateRange: {
        label: 'Date Applied',
      },
      amountRange: {
        label: 'Guarantee Amount',
      },
      status: {
        label: 'Status',
        options: [
          {
            label: 'Paid',
            value: 'PAID',
          },
          {
            label: 'Ongoing',
            value: 'ONGOING',
          },
          {
            label: 'Awaiting Approval',
            value: 'AWAITING_APPROVAL',
          },
          {
            label: 'Awaiting Payment',
            value: 'AWAITING_PAYMENT',
          },
          {
            label: 'Approved',
            value: 'APPROVED',
          },
          {
            label: 'Refunded',
            value: 'REFUNDED',
          },
        ],
      },
    },
  });

  useEffect(() => {
    if (guarantees?.getAllRentGuarantees?.rentGuarantees) {
      setguaranteesRec(guarantees?.getAllRentGuarantees);
    }
  }, [guarantees]);
  const [getExportRecord] = useCustomQuery({
    query: GET_ALL_ADMIN_GUARANTEES,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          filters: {
            from: result.from,
            to: result.to,
            status: result.status,
            channel: result.partner,
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
            tenor: result?.tenor,
          },
        },
      });
      if (output?.data?.getAllCaGuarantees?.guarantees) {
        const rec = cleanUpGuarantees(output?.data?.getAllCaGuarantees?.guarantees);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'LOANS',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  const dropdownData = [{ title: 'Approve Guarantee', color: '#05603A', icon: <ActiveMerchantIcon /> }, { title: 'Decline Guarantee', color: '#D92D20', icon: <DeactivateMerchantIcon /> }];

  return (
    <div>
      <DashboardHeader
        title="Guarantees"
        smallSubTitle={`(${guaranteesRec?.count || 0})`}
        searchPlaceholder="Search by reference no, email address, merchant and customer name "
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={getGuaranteesLoading}
        filterObj={filterObj}
        columnObject={{
          allColumns,
          formatLabels: columnLabelFormat,
          applyChanges,
          selectedColumns,
        }}
        exportObj={{
          handleExport,
        }}
        // disableExport={!adminHasPermission(adminPermissions, 'can-export-guarantees-records')}
        // disableSearch={!adminHasPermission(adminPermissions, 'can-search-guarantees-records')}
        // disbleFilter={!adminHasPermission(adminPermissions, 'can-filter-guarantees-records')}
        payload={payload}

      >
        <AdminLinkTabs>

          <CustomerTabs Tabs={userId ? subTab : Tabs} />

        </AdminLinkTabs>
      </DashboardHeader>

      <RolesAndPermissionGuard permissionName={permissionName} hasErrorTemplate={<PermissionDenied height="60vh" />}>

        <div className="merchant-table-details-cover">
          {guaranteesRec?.count ? (
            <AdminTable
              refreshing={getGuaranteesLoading}
              page={page}
              setPage={setPage}
              pageSize={limit}
              minHeight="calc(100vh - 280px)"
              count={guaranteesRec?.count}
              contents={cleanUpGuarantees(guaranteesRec?.rentGuarantees)}
              reference="id"
              hideDominantImage
              clickable
              hasNameAndImage={hasNameAndImage}
              handleClick={(data) => push(`/guarantees/guarantee-details/${data?.id}`)}
              allowKeyArr={getColumns(true)}
              mobileAllowKeyArr={
                [
                  'status',
                  ['customer', 'name'],
                  ['annualIncome', 'amount'],
                ]
              }
              formatLabels={columnLabelFormat}
              tableStatus={tabStatus}
              dropDownIcon={<HorizontalListIcon color="#667085" />}
              dropdownData={dropdownData}
            />
          ) : (
            <EmptyRecord
              height="calc(100vh - 240px)"
              title={getGuaranteesLoading ? 'Fetching guarantees...' : 'No guarantees found'}
              main="All guarantees will appear here on the dashboard."
              // footer="Receive Payments"
              loading={getGuaranteesLoading}
              Icon={EmptyMerchantSettlement}
            />
          )}

        </div>
      </RolesAndPermissionGuard>
    </div>
  );
};
export default GuaranteesTableRecords;
